import React, { useState, useEffect } from 'react';
import { CourtsRepository } from '../services/repository';
import { showSnackbar } from './Snackbar.jsx';
import './CourtEditor.css';

const CourtEditor = () => {
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCourt, setEditingCourt] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [priorityMode, setPriorityMode] = useState(false);
  const [draggedCourt, setDraggedCourt] = useState(null);
  const [originalCourts, setOriginalCourts] = useState([]);

  useEffect(() => {
    fetchCourts();
  }, []);

  const fetchCourts = async () => {
    try {
      setLoading(true);
      const courtsData = await CourtsRepository.fetchCourts();
      // Sort courts based on current mode
      const sortedCourts = priorityMode 
        ? courtsData.sort((a, b) => (a.ranking || 0) - (b.ranking || 0))
        : courtsData.sort((a, b) => (a.courtNumber || 0) - (b.courtNumber || 0));
      setCourts(sortedCourts);
    } catch (error) {
      console.error('Error fetching courts:', error);
      showSnackbar('Błąd ładowania kortów: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleEditStart = (court) => {
    if (priorityMode) return; // Don't allow editing when in priority mode
    setEditingCourt(court.id);
    setEditValue(court.name);
  };

  const handleEditCancel = () => {
    setEditingCourt(null);
    setEditValue('');
  };

  const handleEditSave = async (courtId) => {
    if (!editValue.trim()) {
      showSnackbar('Nazwa kortu nie może być pusta');
      return;
    }

    try {
      setSaving(true);
      await CourtsRepository.updateCourt(courtId, { name: editValue.trim() });
      
      // Update local state
      setCourts(prev => prev.map(court => 
        court.id === courtId 
          ? { ...court, name: editValue.trim() }
          : court
      ));
      
      setEditingCourt(null);
      setEditValue('');
      showSnackbar('Nazwa kortu została zaktualizowana');
    } catch (error) {
      console.error('Error updating court:', error);
      showSnackbar('Błąd aktualizacji kortu: ' + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e, courtId) => {
    if (e.key === 'Enter') {
      handleEditSave(courtId);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  const togglePriorityMode = () => {
    // Cancel any active edit when switching modes
    if (editingCourt) {
      setEditingCourt(null);
      setEditValue('');
    }
    
    setPriorityMode(!priorityMode);
    if (!priorityMode) {
      // When entering priority mode, store original order and sort by ranking
      setOriginalCourts([...courts]);
      const sortedCourts = [...courts].sort((a, b) => (a.ranking || 0) - (b.ranking || 0));
      setCourts(sortedCourts);
    } else {
      // When exiting priority mode, sort by court number
      const sortedCourts = [...courts].sort((a, b) => (a.courtNumber || 0) - (b.courtNumber || 0));
      setCourts(sortedCourts);
    }
  };

  const handleCancelPriorities = () => {
    // Restore original court order
    setCourts([...originalCourts]);
    setPriorityMode(false);
    setDraggedCourt(null);
  };

  const handleDragStart = (e, court) => {
    if (!priorityMode) return;
    setDraggedCourt(court);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    if (!priorityMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetCourt) => {
    if (!priorityMode || !draggedCourt || draggedCourt.id === targetCourt.id) return;
    
    e.preventDefault();
    
    // Reorder courts locally
    const newCourts = [...courts];
    const draggedIndex = newCourts.findIndex(c => c.id === draggedCourt.id);
    const targetIndex = newCourts.findIndex(c => c.id === targetCourt.id);
    
    // Remove dragged court and insert at new position
    const [draggedItem] = newCourts.splice(draggedIndex, 1);
    newCourts.splice(targetIndex, 0, draggedItem);
    
    // Update rankings based on new order
    const updatedCourts = newCourts.map((court, index) => ({
      ...court,
      ranking: index + 1
    }));
    
    setCourts(updatedCourts);
    setDraggedCourt(null);
  };

  const handleSavePriorities = async () => {
    try {
      setSaving(true);
      
      // Send update requests for all courts
      const updatePromises = courts.map(court => 
        CourtsRepository.updateCourt(court.id, { ranking: court.ranking })
      );
      
      await Promise.all(updatePromises);
      
      setPriorityMode(false);
      
      // Sort by court number after saving
      const sortedCourts = [...courts].sort((a, b) => (a.courtNumber || 0) - (b.courtNumber || 0));
      setCourts(sortedCourts);
      
      showSnackbar('Priorytety mexicano zostały zaktualizowane');
    } catch (error) {
      console.error('Error updating priorities:', error);
      showSnackbar('Błąd aktualizacji priorytetów: ' + (error.message || error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="court-editor">
        <div className="loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Ładowanie...</span>
          </div>
          <p>Ładowanie kortów...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="court-editor">
      <div className="court-editor-header">
        {priorityMode ? (
          <div className="priority-mode-buttons">
            <button
              className="cancel-priorities-btn"
              onClick={handleCancelPriorities}
              disabled={saving}
            >
              Anuluj
            </button>
            <button
              className="save-priorities-btn"
              onClick={handleSavePriorities}
              disabled={saving}
            >
              {saving ? 'Zapisywanie...' : 'Zapisz'}
            </button>
          </div>
        ) : (
          <button
            className="edit-priorities-btn"
            onClick={togglePriorityMode}
          >
            Edytuj priorytety mexicano
          </button>
        )}
      </div>
      
      {priorityMode && (
        <div className="priority-instructions">
          <p>Poustawiaj korty w kolejności od najlepszego do najgorszego poprzez przeciąganie i upuszczanie kortu na liście</p>
        </div>
      )}
      
      <div className="court-list">
        {courts.map((court) => (
          <div 
            key={court.id} 
            className={`court-item ${priorityMode ? 'court-draggable' : ''} ${draggedCourt?.id === court.id ? 'court-dragging' : ''}`}
            draggable={priorityMode}
            onDragStart={(e) => handleDragStart(e, court)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, court)}
          >
            {editingCourt === court.id ? (
              <div className="court-edit-form">
                <button
                  className="cancel-btn"
                  onClick={handleEditCancel}
                  disabled={saving}
                >
                  Anuluj
                </button>
                <input
                  type="text"
                  className="court-name-input"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, court.id)}
                  autoFocus
                  disabled={saving}
                />
                <button
                  className="save-btn"
                  onClick={() => handleEditSave(court.id)}
                  disabled={saving || !editValue.trim()}
                >
                  {saving ? 'Zapisywanie...' : 'Zapisz'}
                </button>
              </div>
            ) : (
              <div className="court-display">
                {!priorityMode && (
                  <button
                    className="edit-btn"
                    onClick={() => handleEditStart(court)}
                    title="Edytuj nazwę kortu"
                  >
                    ✏️
                  </button>
                )}
                <span className="court-name">{court.name}</span>
              </div>
            )}
            
            <div className="court-info">
              <span className="court-number">Kort {court.courtNumber}</span>
              <span className="court-ranking">Priorytet mexicano: {court.ranking}</span>
            </div>
          </div>
        ))}
      </div>
      
      {courts.length === 0 && (
        <div className="no-courts">
          <p>Brak dostępnych kortów</p>
        </div>
      )}
    </div>
  );
};

export default CourtEditor;
